import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { getProductionJobs } from '../services/api';
import { STAGES, fmtDuration, stageDurationMs } from '../components/prodmgmt/stages';
import JobDetailDialog from '../components/prodmgmt/JobDetailDialog';
import { toast } from 'sonner';
import { Factory, CheckCircle2, AlertTriangle, Clock, Boxes } from 'lucide-react';

const COLUMNS = [...STAGES, { key: '__done__', label: 'Completed' }];

const DashboardPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeJobId, setActiveJobId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await getProductionJobs();
      setJobs(res.data);
    } catch (e) {
      toast.error('Failed to load production board');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const openJob = (id) => { setActiveJobId(id); setDetailOpen(true); };

  const columns = {};
  COLUMNS.forEach((c) => { columns[c.key] = []; });
  jobs.forEach((j) => {
    const key = j.status === 'completed' ? '__done__' : j.current_stage;
    if (columns[key]) columns[key].push(j); else columns['__done__'].push(j);
  });

  const inProgress = jobs.filter((j) => j.status !== 'completed').length;
  const completed = jobs.filter((j) => j.status === 'completed').length;
  const delayed = jobs.filter((j) => j.flags && j.flags.delayed).length;
  const totalUnits = jobs.reduce((s, j) => s + (Number(j.units) || 0), 0);

  const currentStageHours = (j) => {
    const st = (j.stages || []).find((s) => s.key === j.current_stage);
    return st ? stageDurationMs(st) : null;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Factory size={24} className="text-accent" />Production Board</h1>
        <p className="text-muted-foreground">Live view of every job by production phase — time in stage and delay flags at a glance</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-sm bg-amber-50 flex items-center justify-center"><Factory size={20} className="text-amber-600" /></div><div><div className="text-2xl font-bold">{inProgress}</div><div className="text-sm text-muted-foreground">In Progress</div></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-sm bg-red-50 flex items-center justify-center"><AlertTriangle size={20} className="text-red-600" /></div><div><div className="text-2xl font-bold text-red-600">{delayed}</div><div className="text-sm text-muted-foreground">Delayed</div></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-sm bg-green-50 flex items-center justify-center"><CheckCircle2 size={20} className="text-green-600" /></div><div><div className="text-2xl font-bold">{completed}</div><div className="text-sm text-muted-foreground">Completed</div></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-sm bg-purple-50 flex items-center justify-center"><Boxes size={20} className="text-purple-600" /></div><div><div className="text-2xl font-bold">{totalUnits}</div><div className="text-sm text-muted-foreground">Total Units</div></div></CardContent></Card>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" /></div>
      ) : jobs.length === 0 ? (
        <Card><CardContent className="flex flex-col items-center justify-center h-48 text-muted-foreground"><Factory size={40} className="mb-3 opacity-50" /><p>No production jobs yet</p><p className="text-sm mt-1">Create jobs in the Production section to see them here</p></CardContent></Card>
      ) : (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-3" style={{ minWidth: 'max-content' }}>
            {COLUMNS.map((col) => {
              const colJobs = columns[col.key] || [];
              const isDone = col.key === '__done__';
              return (
                <div key={col.key} className="w-64 flex-shrink-0" data-testid={`dash-lane-${col.key}`}>
                  <div className={`px-3 py-2 rounded-t-md text-sm font-medium flex items-center justify-between ${isDone ? 'bg-green-600 text-white' : 'bg-secondary text-secondary-foreground'}`}>
                    <span>{col.label}</span>
                    <span className="text-xs bg-white/20 rounded-full px-2">{colJobs.length}</span>
                  </div>
                  <div className="bg-muted/30 rounded-b-md p-2 space-y-2 min-h-[120px]">
                    {colJobs.map((j) => {
                      const delayedJob = j.flags && j.flags.delayed;
                      const hrs = currentStageHours(j);
                      return (
                        <button key={j.id} onClick={() => openJob(j.id)} className={`w-full text-left bg-card border rounded-md p-3 hover:border-accent transition-colors shadow-sm ${delayedJob ? 'border-red-400 bg-red-50/40' : ''}`} data-testid={`dash-job-${j.id}`}>
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-sm font-medium">{j.job_number}</span>
                            {delayedJob && <span className="text-red-600" title={(j.flags.reasons || []).join(', ')} data-testid={`dash-flag-${j.id}`}><AlertTriangle size={14} /></span>}
                          </div>
                          <p className="text-sm truncate mt-0.5">{j.customer}</p>
                          {!isDone && (
                            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Clock size={11} />{hrs != null ? fmtDuration(hrs) + ' in stage' : 'just started'}</p>
                          )}
                          {delayedJob && <p className="text-[11px] text-red-600 mt-1">{(j.flags.reasons || [])[0]}</p>}
                        </button>
                      );
                    })}
                    {colJobs.length === 0 && <p className="text-xs text-muted-foreground text-center py-3">—</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <JobDetailDialog jobId={activeJobId} open={detailOpen} onOpenChange={setDetailOpen} onChanged={fetchJobs} />
    </div>
  );
};

export default DashboardPage;
